import { useState } from 'react';
import PropTypes from 'prop-types';
import { centsToDollar } from '../utils/formatters';

/**
 * Component to display differences between history entries
 * @param {Object} props - Component props
 * @param {Object} props.logData - Log data object containing history entries
 */
const HistoryDiff = ({ logData }) => {
  const [showInitialData, setShowInitialData] = useState(false);

  if (!logData || !logData.h || logData.h.length < 1) {
    return <div className="history-diff-empty">No history changes to display</div>;
  }

  // Extract the history entries
  const historyEntries = logData.h;

  // Find v1 data
  const v1Entry = historyEntries.find(entry => entry.v === 1) || historyEntries[0];

  // Sort entries by version (newest first) - excluding v1 for diffs
  const sortedEntries = [...historyEntries].sort((a, b) => b.v - a.v);

  // Create a consolidated lookup of all previous values by version
  const versionLookup = {};

  // Start with v1 and build up
  const entriesByVersion = [...historyEntries].sort((a, b) => a.v - b.v);

  // Build consolidated state at each version
  let consolidatedState = {};
  entriesByVersion.forEach(entry => {
    consolidatedState = { ...consolidatedState, ...entry.c };
    versionLookup[entry.v] = { ...consolidatedState };
  });

  // Generate diff pairs with proper historical values
  const diffPairs = [];
  for (let i = 0; i < sortedEntries.length - 1; i++) {
    const current = sortedEntries[i];
    const previous = sortedEntries[i + 1];

    // Find all changes in current version
    const diff = {};

    if (current.c) {
      Object.keys(current.c).forEach(key => {
        const newValue = current.c[key];

        // Find the most recent previous value by looking back through versions
        let oldValue = undefined;
        let foundOldValue = false;

        // Check immediate previous version first
        if (previous.c && previous.c.hasOwnProperty(key)) {
          oldValue = previous.c[key];
          foundOldValue = true;
        }

        // If not found, look back through all previous versions
        if (!foundOldValue) {
          for (let v = current.v - 1; v >= 1; v--) {
            if (versionLookup[v] && versionLookup[v].hasOwnProperty(key)) {
              oldValue = versionLookup[v][key];
              foundOldValue = true;
              break;
            }
          }
        }

        // Only add to diff if the value actually changed
        if (newValue !== oldValue) {
          diff[key] = {
            oldValue: oldValue,
            newValue: newValue
          };
        }
      });
    }

    diffPairs.push({
      current,
      previous,
      diff
    });
  }

  const toggleInitialData = () => {
    setShowInitialData(!showInitialData);
  };

  // Format the creation date for display in the header
  const formattedCreationDate = v1Entry ? new Date(v1Entry.ts).toLocaleString() : 'Unknown';

  return (
    <div className="history-diff-container">
      {/* Diffs for changes */}
      {diffPairs.length === 0 ? (
        <div className="history-diff-empty">No changes to display</div>
      ) : (
        diffPairs.map((pair, index) => (
          <DiffItem
            key={index}
            current={pair.current}
            previous={pair.previous}
            diff={pair.diff}
          />
        ))
      )}

      {/* Initial dataset (v1) with collapsible feature */}
      <div className="history-diff-initial">
        <div
          className="history-diff-initial-header"
          onClick={toggleInitialData}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && toggleInitialData()}
        >
          <div className="history-diff-initial-title">
            <h4>Initial Data (Version 1)</h4>
            <span className="history-diff-initial-date">
              Created: {formattedCreationDate}
            </span>
          </div>
          <span className="history-diff-toggle">
            {showInitialData ? '▼' : '►'}
          </span>
        </div>

        {showInitialData && (
          <div className="history-diff-item">
            <div className="history-diff-header">
              <span className="history-diff-date">
                {new Date(v1Entry.ts).toLocaleString()}
              </span>
              <span className="history-diff-version">
                Initial Version
              </span>
            </div>

            <div className="history-diff-initial-data">
              <table className="history-diff-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {v1Entry.c && Object.keys(v1Entry.c).map(key => (
                    <tr key={key}>
                      <td>{formatKey(key)}</td>
                      <td>{formatValue(key, v1Entry.c[key])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Display a single diff item
 */
const DiffItem = ({ current, previous, diff }) => {
  const date = new Date(current.ts);
  const modifier = current.m?._r || current.m?.R || 'system (?)';

  return (
    <div className="history-diff-item">
      <div className="history-diff-header">
        <span className="history-diff-date">
          <strong>{date.toLocaleString()}</strong>
        </span>
        <span className="history-diff-user">
          Modified by: {modifier}
        </span>
        <span className="history-diff-version">
          Version: {current.v}
        </span>
      </div>

      <div className="history-diff-changes">
        {Object.keys(diff).length === 0 ? (
          <p>No changes detected</p>
        ) : (
          <table className="history-diff-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Previous Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(diff).map(key => (
                <tr key={key}>
                  <td>{formatKey(key)}</td>
                  <td className="history-diff-old-value">
                    {formatValue(key, diff[key].oldValue)}
                  </td>
                  <td className="history-diff-new">
                    {formatValue(key, diff[key].newValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Format functions moved outside component to be reused
const formatKey = (key) => {
  return key
    .replace(/Cents$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatValue = (key, value) => {
  if (value === null || value === undefined) {
    return '-';
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (key.includes('_cents') || key.endsWith('Cents')) {
    return centsToDollar(value);
  }

  return value;
};

HistoryDiff.propTypes = {
  logData: PropTypes.shape({
    h: PropTypes.arrayOf(PropTypes.shape({
      c: PropTypes.object, // content
      m: PropTypes.object, // metadata
      v: PropTypes.number, // version
      ts: PropTypes.number  // timestamp
    })),
    v: PropTypes.number
  })
};

export default HistoryDiff;
